import QueryService from "../../services/queryService";
import Query from "../query";
import {IVOptionSetEndpoint, IVOptionSetLocal, TVOptionSet} from "~/interfaces/vSelectInterfaces";
import {ObjectHelper} from "~/helpers/objectHelper";
import {Logger} from "~/services/logger"
import {Hash} from "~/interfaces/hashInterfaces"

export default class VOptionSet<T extends Hash = object> implements IVOptionSetLocal<T>, IVOptionSetEndpoint<T> {
  originalOptions: any[] = [];
  optionType: any = null;
  _options: any[] = this.originalOptions;
  endpoint: string = null;
  valueKey: string & keyof T = 'value';
  altKey: string = 'value';
  labelKey: string & keyof T | ((arg?: T) => string) = 'label';
  placeholder: string & keyof T | ((arg?: T) => string) = null;
  rootKey: string = 'result';
  heading: string = null;
  isPinned: boolean = false;
  query: Query = new Query();
  queryService: QueryService = new QueryService({endpoint: this.endpoint});
  selectedOption: any;
  selectedColor: string = null;
  searchStatus: string = 'Search for results...';
  actionButton: string | ((arg?: any) => string) = null;
  valueComparer: (a: any, b: any) => boolean;
  icon: string = null;
  iconKey: string | ((arg?: any) => string) = null;
  showWhenEmpty: boolean = true;
  endpointOptionFilter: null | ((option: any) => boolean);
  endpointOptionProcessor: null | ((option: any) => boolean);
  emptySearchStatus: string | null;

  constructor(props: TVOptionSet = null) {
    if (ObjectHelper.isHash(props)) {
      Object.getOwnPropertyNames(props).forEach(key => {
        switch (key) {
          case 'options':
            // need optionType to be set before options
            break;
          case 'endpointOptions':
            // endpointOptions handled with endpoint
            break;
          case 'endpoint':
            if (this.isVOptionSetEndpoint(props)) {
              this[key] = props[key];
              this.queryService = new QueryService({endpoint: props[key], options: props.endpointOptions});
            }
            break;
          case 'endpointFilters':
            this.query.setFilters(props[key]);
            break;
          default:
            this[key] = props[key];
        }
      });
      // need optionType to be set before options (should be handled in switch statement above)
      if (props.options) {
        this.options = props.options;
        this.originalOptions = this.options;
      }
    }
    this.initSearchStatus();
  }
  get selectedLabelSuffix(): string{
    return this.selectedOption?.labelSuffix || ""
  }
  get options(): any[] {
    return this._options;
  }
  set options(data) {
    data.forEach(datum => datum.optionType = this.optionType);
    this._options = data;
  }
  get isAlwaysEmpty(): boolean {
    return this.endpoint === null && this.originalOptions.length === 0;
  }
  get hasOptions(): boolean {
    return this.options.length > 0;
  }
  get pinnedOptions(): any[] {
    return this.originalOptions.filter(o => o.isPinned);
  }
  filter(q: string) {
    if (this.isPinned) {
      return this.originalOptions;
    } else {
      return this.originalOptions.filter(o => {
        return o.isPinned || this.getLabel(o).toLowerCase().includes(q.toLowerCase());
      });
    }
  }
  search(q: string): Promise<{}> {
    this.searchStatus = 'Searching...';
    this.query.setQ(q);
    if (this.endpoint) {
      const promise = this.queryService.fetch(this.query);
      return promise.then((results) => {
        let optionsFromEndpoint = this.rootKey === undefined ? results : results[this.rootKey];
        if (this.endpointOptionFilter) {
          optionsFromEndpoint = optionsFromEndpoint.filter(this.endpointOptionFilter);
        }
        if (!!this.endpointOptionProcessor) {
          optionsFromEndpoint = optionsFromEndpoint.map(option => this.endpointOptionProcessor(option))
        }
        this.options = [...this.pinnedOptions, ...optionsFromEndpoint];
        this.updateSearchStatus();
        return promise;
      })
        .catch(e => {
          Logger.errorOnce(e)
          this.searchStatus = 'Search failed'
        })
    } else {
      let results = {};
      this.options = this.filter(this.query.q);
      if (this.rootKey === undefined) {
        results = this.options;
      } else {
        results[this.rootKey] = this.options;
      }
      this.updateSearchStatus();
      return new Promise((resolve, reject) => resolve(results));
    }
  }
  private initSearchStatus(): void {
    if (this.isAlwaysEmpty) {
      this.searchStatus = 'Empty...';
    } else {
      this.searchStatus = 'Search for results...';
    }
  }
  private updateSearchStatus(): void {
    if (this.hasOptions) {
      this.searchStatus = 'Results found';
    } else if (this.isAlwaysEmpty) {
      this.searchStatus = 'Empty...';
    } else if (this.emptySearchStatus) {
      this.searchStatus = this.emptySearchStatus;
    } else {
      this.searchStatus = 'No matching results...';
    }
  }
  getLabel(o): string {
    if (typeof this.labelKey === 'function') {
      return this.labelKey(o);
    } else {
      return this.labelKey
        .split('.')
        .reduce((label, current) => label[current], o);
    }
  }
  getIcon(o): string {
    if (typeof this.iconKey === 'function') {
      return this.iconKey(o);
    } else {
      return this.iconKey
        .split('.')
        .reduce((key, current) => key[current], o);
    }
  }
  getActionButtonLabel(o): string {
    if (typeof this.actionButton === 'function') {
      return this.actionButton(o);
    } else {
      return this.actionButton;
    }
  }
  getPlaceholder(o): string {
    if (typeof this.placeholder === 'function') {
      return this.placeholder(o);
    } else {
      return this.placeholder;
    }
  }
  resetData() {
    this.initSearchStatus();
    this.options = this.originalOptions;
  }
  private isVOptionSetLocal(set: TVOptionSet): set is IVOptionSetLocal {
    return !set.hasOwnProperty('endpoint');
  }
  private isVOptionSetEndpoint(set: TVOptionSet): set is IVOptionSetEndpoint {
    return set.hasOwnProperty('endpoint');
  }
}